import React, { useState } from 'react';
import { setCookie, getCookie } from '@utility/cookie';
import { printHTML } from '@utility/Api';
import { PUBLIC_URLS } from '@components/link-utils';
import useLangAuth from '@hooks/useLangAuth';
import { COOKIE, cookie_policy, COOKIE_POLICY_KEY } from '@utility/const';
import useToggle from '@hooks/useToggle';
import { addMoment } from '@components/utils/momentUtils';
import ButtonV2 from './ButtonV2';

type CookieBannerProps = {
  trackCookieBanner?: (open: boolean) => void;
};

const langKeys = [
  'COOKIE_BANNER_DESC',
  'COOKIE_POLICY',
  'MANAGE_COOKIES',
  'REFUSE',
  'ACCEPT_ALL_COOKIES',
] as const;

const CookieBanner = ({ trackCookieBanner }: CookieBannerProps, forwardedRef) => {
  let sameSite = "";
  let secureAttribute = false
  const cmsPreview = getCookie(COOKIE.CMS_PREVIEW);
  if(cmsPreview) {sameSite = "None"; secureAttribute = true};

  const [modalManageOpen, toggleModalManageOpen] = useToggle(false);
  const [trackingAccepted, setTracking] = useState(
    !!JSON.parse(getCookie(COOKIE_POLICY_KEY) || '{}')?.accepted
  );

  const labels = useLangAuth(langKeys);

  React.useEffect(() => {
    trackCookieBanner?.(trackingAccepted);
  }, [trackingAccepted]);

  const handleAcceptPolicy = () => {
    const cookie = JSON.parse(getCookie(COOKIE_POLICY_KEY) || JSON.stringify(cookie_policy));
    const cookiePolicyData = JSON.stringify({ ...cookie, accepted: true });
    setCookie(COOKIE_POLICY_KEY, cookiePolicyData, addMoment(6, 'months'), "", secureAttribute, sameSite); //moment().add(6, 'months')
    setTracking(true);
  };

  const handleRefusePolicy = () => {
    const refuseCookie = cloneDeep(cookie_policy);
    //refuse all possible cookies
    for (let key in refuseCookie) {
      refuseCookie[key] = false;
    }
    const cookiePolicyData = JSON.stringify({ ...refuseCookie, accepted: true });
    setCookie(COOKIE_POLICY_KEY, cookiePolicyData, addMoment(6, 'months'), "", secureAttribute, sameSite); //moment().add(6, 'months') //
    toggleModalManageOpen(false);
    setTracking(true);
  };

  if (!trackingAccepted && !modalManageOpen) {
    return (
      <>
        <div className="cookie-banner" ref={forwardedRef} role="dialog">
          <div className="cookie-banner__wrapper max-width-1440">
            <section role="region" aria-label={labels?.COOKIE_BANNER_DESC}>
              <p
                className="cookie-banner__description"
                dangerouslySetInnerHTML={printHTML(
                  labels?.COOKIE_BANNER_DESC.replace('cookie policy', labels?.COOKIE_POLICY)
                    .replace(
                      '{',
                      `<a class="text-btn-no-arrow small inline" aria-label="${labels?.COOKIE_POLICY}" href="${PUBLIC_URLS.LEGAL.URL}">`
                    )
                    .replace('}', '</a>')
                )}
              />
            </section>
            <section className="cookie-banner__buttons">
              <div>
                <ButtonV2
                  variant="primary"
                  small
                  handleClick={handleAcceptPolicy}
                  className="cookie-banner__button accept"
                  ariaLabel={labels?.ACCEPT_ALL_COOKIES}
                >
                  {labels?.ACCEPT_ALL_COOKIES}
                </ButtonV2>
                <ButtonV2
                  variant="secondary"
                  small
                  handleClick={() => handleRefusePolicy()}
                  className="cookie-banner__button refuse"
                  ariaLabel={labels?.REFUSE}
                >
                  {labels?.REFUSE}
                </ButtonV2>
              </div>
              <ButtonV2
                variant="text-btn-no-arrow"
                small
                className="cookie-banner__button-link"
                handleClick={() => toggleModalManageOpen(true)}
                to={PUBLIC_URLS.MANAGE_COOKIES.URL}
                ariaLabel={labels?.MANAGE_COOKIES}
              >
                {labels?.MANAGE_COOKIES}
              </ButtonV2>
            </section>
          </div>
        </div>
      </>
    );
  }
  return null;
};

export default React.forwardRef(CookieBanner);
